<template>
  <HrPageLayout
    title="Records Management"
    subtitle="Record and track staff files: available, missing, transferred or closed"
    icon="📁"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'Staff Establishment' },
      { label: 'File Registry' }
    ]"
  >
    <template #header-actions>
      <button class="hr-btn hr-btn-primary" @click="openAddModal">
        <i class="bx bx-plus"></i> Add File Record
      </button>
    </template>

    <div class="file-registry-content">
      <!-- Stats -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-folder"></i></div>
          <p class="hr-metric-label">Total Records</p>
          <h2 class="hr-metric-value">{{ stats.total }}</h2>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <p class="hr-metric-label">Available</p>
          <h2 class="hr-metric-value">{{ stats.available }}</h2>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-error-circle"></i></div>
          <p class="hr-metric-label">Missing</p>
          <h2 class="hr-metric-value">{{ stats.missing }}</h2>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-transfer"></i></div>
          <p class="hr-metric-label">Transferred</p>
          <h2 class="hr-metric-value">{{ stats.transferred }}</h2>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-folder-minus"></i></div>
          <p class="hr-metric-label">Closed</p>
          <h2 class="hr-metric-value">{{ stats.closed }}</h2>
        </div>
      </div>

      <!-- Filters -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body hr-py-3">
          <div class="hr-filters-row">
            <div class="hr-search-wrap">
              <input
                v-model="search"
                type="text"
                class="hr-input"
                placeholder="Search by record number or employee name..."
                spellcheck="false"
                @input="debouncedLoad"
              >
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Status</label>
              <select v-model="filterStatus" @change="loadRecords" class="hr-select" style="min-width: 160px;">
                <option value="">All statuses</option>
                <option value="available">Available</option>
                <option value="missing">Missing</option>
                <option value="transferred">Transferred</option>
                <option value="closed">Closed</option>
                <option value="exit">Exit</option>
                <option value="deceased">Deceased</option>
                <option value="retired">Retired</option>
                <option value="transferred out">Transferred Out</option>
              </select>
              <select v-model="filterDepartmentId" @change="loadRecords" class="hr-select" style="min-width: 220px;">
                <option value="">All departments</option>
                <option v-for="d in departments" :key="'fd-' + d.id" :value="String(d.id)">{{ d.departmentName }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="hr-chart-card">
        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-hover hr-table-striped">
              <thead>
                <tr>
                  <th>Record Number</th>
                  <th>Employee</th>
                  <th>Emp No</th>
                  <th>Department</th>
                  <th>Employment Date</th>
                  <th>Status</th>
                  <th>Remarks</th>
                  <th>Transaction Date</th>
                  <th>File(s)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="10" class="hr-text-center hr-py-4">
                    <div class="hr-spinner"></div>
                  </td>
                </tr>
                <tr v-else-if="filteredRecords.length === 0">
                  <td colspan="10" class="hr-text-center hr-py-4 hr-text-muted">
                    No records found
                  </td>
                </tr>
                <tr v-for="row in filteredRecords" :key="row.id">
                  <td><strong>{{ row.record_number }}</strong></td>
                  <td>{{ row.employee_name || '—' }}</td>
                  <td><span class="hr-badge hr-badge-secondary">{{ row.emp_no || row.empNo || '—' }}</span></td>
                  <td>{{ row.department_name || row.departmentName || '—' }}</td>
                  <td>{{ formatDate(row.employment_date || row.employmentDate) }}</td>
                  <td>
                    <span :class="'hr-badge ' + statusBadgeClass(row.status)">{{ formatStatus(row.status) }}</span>
                  </td>
                  <td class="hr-text-muted hr-small">{{ (row.notes || '').slice(0, 50) }}{{ (row.notes && row.notes.length > 50) ? '…' : '' }}</td>
                  <td>{{ formatDate(row.status_date) }}</td>
                  <td>
                    <div v-if="getFilesListFromRow(row).length" class="registry-file-list">
                      <div v-for="(file, idx) in getFilesListFromRow(row)" :key="idx" class="registry-file-item">
                        <div class="registry-file-info">
                          <i class="bx bx-file registry-file-icon"></i>
                          <span class="registry-file-name" :title="getFileName(file)">{{ getFileName(file) }}</span>
                        </div>
                        <div class="hr-btn-group">
                          <button
                            type="button"
                            class="hr-btn hr-btn-secondary btn-compact"
                            @click="openRegistryFileByIndex(idx, row.id)"
                            title="View File"
                          >
                            <i class="bx bx-show"></i> View
                          </button>
                          <button
                            type="button"
                            class="hr-btn hr-btn-info btn-compact"
                            @click="downloadRegistryFileByIndex(idx, file, row)"
                            title="Download File"
                          >
                            <i class="bx bx-download"></i> Download
                          </button>
                        </div>
                      </div>
                    </div>
                    <span v-else class="hr-text-muted hr-small">—</span>
                  </td>
                  <td>
                    <div class="hr-btn-group">
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="editRecord(row)" title="Edit">
                        <i class="bx bx-edit"></i> Edit Record
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Add/Edit Modal -->
      <ModalWrapper ref="formModal" :title="editingId ? 'Edit File Record' : 'Add File Record'" size="large">
        <template #body>
          <form @submit.prevent="saveRecord" class="hr-form-grid">
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Record Number <span class="hr-text-danger">*</span></label>
              <input
                v-model="form.record_number"
                type="text"
                class="hr-input"
                placeholder="e.g. HR-2024-001"
                required
                :disabled="!!editingId"
              >
              <small v-if="editingId" class="hr-text-muted">Record number cannot be changed after creation.</small>
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Employee <span class="hr-text-danger">*</span></label>
              <input
                v-model.trim="employeePickerSearch"
                type="text"
                class="hr-input hr-mb-2"
                placeholder="Search by employee number, national ID, or name..."
                :disabled="!!editingId"
              >
              <select
                v-model="form.employee_id"
                class="hr-select"
                required
                :disabled="!!editingId"
              >
                <option value="">Select employee</option>
                <option v-for="emp in filteredEmployeesForPicker" :key="emp.id" :value="emp.id">
                  {{ [emp.firstName, emp.middleName || emp.middle_name, emp.lastName].filter(Boolean).join(' ') }} ({{ emp.empNo || emp.emp_no || emp.employeeNo || emp.employee_number || emp.employeeNumber || emp.personalNo || emp.payrollNo || emp.nationalId || emp.national_id }})
                </option>
              </select>
              <small v-if="employeePickerSearch && !filteredEmployeesForPicker.length" class="hr-text-muted">
                No employee matches "{{ employeePickerSearch }}".
              </small>
              <small v-if="!employees.length && !loadingEmployees" class="hr-text-muted">No employees loaded. Try refreshing.</small>
            </div>

            <!-- Department & Employment Date (Read Only) -->
            <div v-if="selectedEmployeeDetails" class="hr-filter-group span-half">
              <label class="hr-form-label">Department</label>
              <input
                :value="selectedEmployeeDetails.departmentName || selectedEmployeeDetails.department_name || '—'"
                type="text"
                class="hr-input"
                readonly
                disabled
              >
            </div>
            <div v-if="selectedEmployeeDetails" class="hr-filter-group span-half">
              <label class="hr-form-label">Employment Date</label>
              <input
                :value="formatDate(selectedEmployeeDetails.employmentDate || selectedEmployeeDetails.employment_date || selectedEmployeeDetails.hireDate)"
                type="text"
                class="hr-input"
                readonly
                disabled
              >
            </div>

            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Status <span class="hr-text-danger">*</span></label>
              <select v-model="form.status" class="hr-select" required @change="onStatusChange">
                <option value="available">Available</option>
                <option value="missing">Missing</option>
                <option value="transferred">Transferred</option>
                <option value="closed">Closed</option>
                <option value="exit">Exit</option>
                <option value="deceased">Deceased</option>
                <option value="retired">Retired</option>
                <option value="transferred out">Transferred Out</option>
              </select>
            </div>
            <div v-if="form.status === 'transferred' || form.status === 'transferred out'" class="hr-filter-group span-full">
              <label class="hr-form-label">Transferred To</label>
              <input v-model="form.transferred_to" type="text" class="hr-input" placeholder="Where was the file transferred?">
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Transaction Date</label>
              <input v-model="form.status_date" type="date" class="hr-input">
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Remarks</label>
              <textarea v-model="form.notes" class="hr-textarea" rows="3" placeholder="Optional remarks"></textarea>
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Employee files (PDF, ZIP, Word, Images)</label>
              <input
                ref="pdfFileInput"
                type="file"
                class="hr-input"
                accept=".pdf,.zip,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/zip,image/*"
                multiple
                @change="onPdfFilesChange"
              >
              <small class="hr-text-muted hr-d-block hr-mt-1">
                Optional. Multiple files allowed. Upload PDF, ZIP, Word documents, or images.
              </small>
              <div v-if="pdfFiles.length" class="hr-mt-2 hr-flex-col" style="gap: 4px;">
                <div v-for="(f, i) in pdfFiles" :key="i" class="hr-flex-between">
                  <span class="hr-badge hr-badge-success"><i class="bx bx-file"></i> {{ f.name }}</span>
                  <button type="button" class="hr-btn hr-btn-link hr-btn-sm hr-text-danger" @click.prevent="removeSelectedFile(i)">
                    <i class="bx bx-x"></i> Remove
                  </button>
                </div>
              </div>
              <div v-if="editingId && getFilesList().length" class="hr-mt-3">
                <label class="hr-form-label hr-small">Currently Uploaded Files:</label>
                <div class="modal-file-list" style="margin-top: 6px;">
                  <div v-for="(file, idx) in getFilesList()" :key="idx" class="modal-file-item">
                    <span class="hr-small text-truncate" style="max-width: 250px; font-weight: 500;" :title="getFileName(file)">
                      <i class="bx bx-file" style="color: #059669; font-size: 16px; margin-right: 4px;"></i> {{ getFileName(file) }}
                    </span>
                    <div class="hr-btn-group">
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" @click.prevent="openRegistryFileByIndex(idx)">
                        <i class="bx bx-show"></i> View
                      </button>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-info" @click.prevent="downloadRegistryFileByIndex(idx, file)">
                        <i class="bx bx-download"></i> Download
                      </button>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-danger" @click.prevent="deleteRegistryFileByIndex(idx)">
                        <i class="bx bx-trash"></i> Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.formModal?.close()">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" @click="saveRecord" :disabled="saving">
            <span v-if="saving" class="hr-spinner small"></span>
            <i v-else class="bx bx-check"></i> {{ editingId ? 'Update' : 'Save' }}
          </button>
        </template>
      </ModalWrapper>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { execute, executeDownload, authUrl } from "@/api";

export default {
  name: "FileRegistry",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      records: [],
      employees: [],
      departments: [],
      loading: false,
      loadingEmployees: false,
      saving: false,
      search: "",
      filterStatus: "",
      filterDepartmentId: "",
      employeePickerSearch: "",
      editingId: null,
      form: {
        record_number: "",
        employee_id: "",
        status: "available",
        notes: "",
        transferred_to: "",
        status_date: new Date().toISOString().slice(0, 10)
      },
      pdfFiles: [],
      existingPdfPath: null,
      debounceTimer: null,
      employeeSearchTimer: null
    };
  },
  computed: {
    filteredEmployeesForPicker() {
      const list = Array.isArray(this.employees) ? this.employees : [];
      const q = (this.employeePickerSearch || "").trim().toLowerCase();
      if (!q) return list;
      return list.filter((emp) => {
        const searchable = this.buildEmployeeSearchText(emp);
        return searchable.includes(q);
      });
    },
    filteredRecords() {
      return this.records;
    },
    selectedEmployeeDetails() {
      if (!this.form.employee_id) return null;
      return this.employees.find((emp) => String(emp.id) === String(this.form.employee_id));
    },
    stats() {
      const s = { total: 0, available: 0, missing: 0, transferred: 0, closed: 0 };
      this.records.forEach((r) => {
        s.total++;
        const st = (r.status || "").toLowerCase();
        if (st === "available") s.available++;
        else if (st === "missing") s.missing++;
        else if (st === "transferred" || st === "transferred out") s.transferred++;
        else if (st === "closed" || st === "exit" || st === "deceased" || st === "retired") s.closed++;
      });
      return s;
    }
  },
  mounted() {
    this.loadRecords();
    this.loadDepartments();
  },
  watch: {
    employeePickerSearch() {
      this.debouncedEmployeeSearch();
    },
    employees() {
      this.autoSelectEmployeeFromSearch();
    }
  },
  methods: {
    debouncedLoad() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.loadRecords(), 300);
    },
    debouncedEmployeeSearch() {
      if (this.employeeSearchTimer) clearTimeout(this.employeeSearchTimer);
      this.employeeSearchTimer = setTimeout(() => {
        this.loadEmployees(this.employeePickerSearch);
      }, 300);
    },
    async loadRecords() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getStaffFileRegistry");
        data.append("keyword", this.search);
        if (this.filterStatus) data.append("status", this.filterStatus);
        if (this.filterDepartmentId) data.append("departmentId", this.filterDepartmentId);
        const res = await execute(data, authUrl);
        if (res?.data?.success && Array.isArray(res.data.data?.records)) {
          this.records = res.data.data.records;
        } else {
          this.records = res?.data?.records ?? [];
        }
      } catch (e) {
        console.error("File registry load error:", e);
        this.records = [];
      } finally {
        this.loading = false;
      }
    },
    async loadDepartments() {
      try {
        const data = new FormData();
        data.append("function", "getDepartments");
        const res = await execute(data, authUrl);
        this.departments = res?.data?.departments ?? res?.data?.data?.departments ?? [];
      } catch (e) {
        console.error("Departments load error:", e);
        this.departments = [];
      }
    },
    async loadEmployees(keyword = "") {
      this.loadingEmployees = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployees");
        data.append("page", "1");
        data.append("rows_per_page", "100");
        const searchText = String(keyword || "").trim();
        if (searchText) data.append("keyword", searchText);
        const res = await execute(data, authUrl);
        const list = res?.data?.employees ?? res?.data?.data?.employees ?? res?.data ?? [];
        this.employees = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error("Employees load error:", e);
        this.employees = [];
      } finally {
        this.loadingEmployees = false;
        this.autoSelectEmployeeFromSearch();
      }
    },
    getEmployeeSearchValue(emp, key) {
      if (key === "employeeNo") {
        return String(
          emp?.empNo ||
          emp?.emp_no ||
          emp?.employeeNo ||
          emp?.employee_number ||
          emp?.employeeNumber ||
          emp?.personalNo ||
          emp?.payrollNo ||
          ""
        ).trim();
      }
      if (key === "nationalId") {
        return String(emp?.nationalId || emp?.national_id || "").trim();
      }
      if (key === "fullName") {
        const firstName = String(emp?.firstName || "").trim();
        const middleName = String(emp?.middleName || emp?.middle_name || "").trim();
        const lastName = String(emp?.lastName || "").trim();
        return `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, " ").trim();
      }
      return "";
    },
    buildEmployeeSearchText(emp) {
      const fullName = this.getEmployeeSearchValue(emp, "fullName");
      const employeeNo = this.getEmployeeSearchValue(emp, "employeeNo");
      const nationalId = this.getEmployeeSearchValue(emp, "nationalId");
      const detailBits = [
        fullName,
        employeeNo,
        nationalId,
        emp?.email,
        emp?.phone,
        emp?.mobile,
        emp?.designation,
        emp?.position,
        emp?.departmentName,
        emp?.department_name
      ]
        .map((v) => String(v || "").trim())
        .filter(Boolean);
      return detailBits.join(" ").toLowerCase();
    },
    autoSelectEmployeeFromSearch() {
      if (this.editingId) return;
      const query = String(this.employeePickerSearch || "").trim();
      if (!query) return;

      const qLower = query.toLowerCase();
      const list = this.filteredEmployeesForPicker || [];
      if (!list.length) return;

      // Prefer exact employee number / national ID matches.
      const exactMatches = list.filter((emp) => {
        const employeeNo = this.getEmployeeSearchValue(emp, "employeeNo").toLowerCase();
        const nationalId = this.getEmployeeSearchValue(emp, "nationalId").toLowerCase();
        return employeeNo === qLower || nationalId === qLower;
      });

      if (exactMatches.length === 1) {
        this.form.employee_id = exactMatches[0].id;
        return;
      }

      // If there is only one filtered result, auto-select it.
      if (list.length === 1) {
        this.form.employee_id = list[0].id;
      }
    },
    openAddModal() {
      this.editingId = null;
      this.employeePickerSearch = "";
      this.pdfFiles = [];
      this.existingPdfPath = null;
      this.form = {
        record_number: "",
        employee_id: "",
        status: "available",
        notes: "",
        transferred_to: "",
        status_date: new Date().toISOString().slice(0, 10)
      };
      this.loadEmployees("");
      this.$refs.formModal?.open();
      this.$nextTick(() => this.resetPdfInput());
    },
    editRecord(row) {
      this.editingId = row.id;
      this.employeePickerSearch = "";
      this.pdfFiles = [];
      this.existingPdfPath = row.pdf_path || row.pdfPath || null;
      this.form = {
        record_number: row.record_number,
        employee_id: String(row.employee_id),
        status: (row.status || "available").toLowerCase(),
        notes: row.notes || "",
        transferred_to: row.transferred_to || "",
        status_date: row.status_date ? row.status_date.slice(0, 10) : new Date().toISOString().slice(0, 10)
      };
      if (!this.employees.length) this.loadEmployees("");
      this.$refs.formModal?.open();
      this.$nextTick(() => this.resetPdfInput());
    },
    onPdfFilesChange(e) {
      const selected = Array.from(e.target.files || []);
      this.pdfFiles = [...this.pdfFiles, ...selected];
    },
    removeSelectedFile(index) {
      this.pdfFiles.splice(index, 1);
      this.resetPdfInput();
    },
    resetPdfInput() {
      const el = this.$refs.pdfFileInput;
      if (el) el.value = "";
    },
    getFilesList() {
      const path = this.existingPdfPath;
      if (!path) return [];
      try {
        const decoded = JSON.parse(path);
        if (Array.isArray(decoded)) return decoded;
      } catch (e) {
        // Fallback to single string path if it's not a JSON array
      }
      return [path];
    },
    getFilesListFromRow(row) {
      const path = row.pdf_path || row.pdfPath || "";
      if (!path) return [];
      try {
        const decoded = JSON.parse(path);
        if (Array.isArray(decoded)) return decoded;
      } catch (e) {
        // Fallback to single string path if it's not a JSON array
      }
      return [path];
    },
    getFileName(path) {
      if (!path) return "file";
      const filename = path.split('/').pop();
      // 1. Try to match new format with original filename: fr_[id]_[time]_[idx]_[original_name].[ext]
      const matchNew = filename.match(/^fr_\d+_\d+_\d+_(.+)$/);
      if (matchNew) return matchNew[1];
      
      // 2. Try to match format with index: fr_[id]_[time]_[idx].[ext]
      const matchIdx = filename.match(/^fr_\d+_\d+_(\d+)\.(.+)$/);
      if (matchIdx) {
        return `File_${Number(matchIdx[1]) + 1}.${matchIdx[2]}`;
      }
      
      // 3. Try to match legacy single file: fr_[id]_[time].[ext]
      const matchLegacy = filename.match(/^fr_\d+_\d+\.(.+)$/);
      if (matchLegacy) {
        return `File.${matchLegacy[1]}`;
      }
      
      // 4. Default fallback
      return filename;
    },
    async openRegistryFileByIndex(index, rowId = null) {
      const id = rowId || this.editingId;
      if (!id) return;
      try {
        const data = new FormData();
        data.append("function", "downloadStaffFileRegistryPdf");
        data.append("id", String(id));
        data.append("file_index", String(index));
        const res = await executeDownload(data, authUrl);
        const blob = res.data;
        if (blob && blob.type && blob.type.indexOf("json") !== -1) {
          const text = await blob.text();
          try {
            const j = JSON.parse(text);
            window.alert(j.message || "Could not open file.");
          } catch (_) {
            window.alert("Could not open file.");
          }
          return;
        }
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (err) {
        window.alert(err?.message || "Could not open file.");
      }
    },
    async downloadRegistryFileByIndex(index, filePath, row = null) {
      const id = row ? row.id : this.editingId;
      const recordNumber = row ? row.record_number : this.form.record_number;
      if (!id) return;
      try {
        const data = new FormData();
        data.append("function", "downloadStaffFileRegistryPdf");
        data.append("id", String(id));
        data.append("file_index", String(index));
        const res = await executeDownload(data, authUrl);
        const blob = res.data;
        if (blob && blob.type && blob.type.indexOf("json") !== -1) {
          const text = await blob.text();
          try {
            const j = JSON.parse(text);
            window.alert(j.message || "Could not download file.");
          } catch (_) {
            window.alert("Could not download file.");
          }
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const ext = filePath.split('.').pop().toLowerCase() || "pdf";
        const baseName = (recordNumber || "record").replace(/[^A-Za-z0-9._-]/g, "_");
        a.href = url;
        a.download = `${baseName}_${index + 1}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (err) {
        window.alert(err?.message || "Could not download file.");
      }
    },
    async deleteRegistryFileByIndex(index) {
      if (!this.editingId) return;
      if (!window.confirm("Delete this uploaded file?")) return;
      try {
        const data = new FormData();
        data.append("function", "deleteStaffFileRegistryPdf");
        data.append("id", String(this.editingId));
        data.append("file_index", String(index));
        const res = await execute(data, authUrl);
        if (res?.data?.success === false) {
          window.alert(res?.data?.message || "Could not delete file.");
          return;
        }
        const list = this.getFilesList();
        list.splice(index, 1);
        if (list.length === 0) {
          this.existingPdfPath = null;
        } else {
          this.existingPdfPath = JSON.stringify(list);
        }
        await this.loadRecords();
      } catch (err) {
        window.alert(err?.message || "Could not delete file.");
      }
    },
    onStatusChange() {
      if (this.form.status !== "transferred" && this.form.status !== "transferred out") {
        this.form.transferred_to = "";
      }
    },
    async saveRecord() {
      if (!this.form.record_number || !this.form.employee_id || !this.form.status) return;
      this.saving = true;
      try {
        const data = new FormData();
        data.append("function", "saveStaffFileRegistry");
        data.append("record_number", this.form.record_number);
        data.append("employee_id", this.form.employee_id);
        data.append("status", this.form.status);
        data.append("notes", this.form.notes);
        data.append("transferred_to", this.form.transferred_to || "");
        data.append("status_date", this.form.status_date || "");
        if (this.editingId) data.append("id", String(this.editingId));
        
        if (this.pdfFiles && this.pdfFiles.length > 0) {
          this.pdfFiles.forEach((file) => {
            data.append("pdfFiles[]", file);
          });
        }
        
        const res = await execute(data, authUrl);
        if (res?.data?.success !== false) {
          this.pdfFiles = [];
          this.resetPdfInput();
          this.$refs.formModal?.close();
          this.loadRecords();
        } else {
          alert(res?.data?.message || "Failed to save.");
        }
      } catch (e) {
        console.error("Save error:", e);
        alert(e?.message || "Failed to save.");
      } finally {
        this.saving = false;
      }
    },
    formatStatus(s) {
      if (!s) return "—";
      if (s.toLowerCase() === "transferred out") return "Transferred Out";
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    statusBadgeClass(s) {
      const t = (s || "").toLowerCase();
      if (t === "available") return "hr-badge-success";
      if (t === "missing") return "hr-badge-danger";
      if (t === "transferred" || t === "transferred out") return "hr-badge-warning";
      if (t === "closed" || t === "exit" || t === "deceased" || t === "retired") return "hr-badge-secondary";
      return "hr-badge-info";
    },
    formatDate(d) {
      if (!d) return "—";
      return d.slice(0, 10);
    },
    goToEmployee(employeeId) {
      if (employeeId) {
        sessionStorage.setItem("employeeId", employeeId);
        this.$router.push("/employee-details-view");
      }
    }
  }
};
</script>

<style scoped>
.file-registry-content { max-width: 100%; }
.hr-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.span-half { grid-column: span 1; }
.span-full { grid-column: 1 / -1; }

.hr-flex-col {
  display: flex;
  flex-direction: column;
}

.registry-file-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 4px 0;
}

.registry-file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #f8fafc;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.registry-file-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.registry-file-info {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.registry-file-icon {
  font-size: 16px;
  color: #059669;
  flex-shrink: 0;
}

.registry-file-name {
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 130px;
}

.hr-btn.btn-compact {
  padding: 4px 8px;
  font-size: 11px;
  border-radius: 6px;
  gap: 4px;
}

.hr-btn.btn-compact i {
  font-size: 13px;
}

.modal-file-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.modal-file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #f8fafc;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.modal-file-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}
</style>
